import { observer } from 'mobx-react-lite';
import styles from './index.module.scss';
import {
  assistanceLvlOptions,
  eventType,
  eventContactMethodOptions,
} from '../events-fields-options';
import EventTimeWarning from '../Text/EventTimeWarning';
import DecisionText from '../Text/DecisionText';
import {
  commentMonitorConditionsText,
  commentMonitorInitialText,
  educationToInitialText,
} from '../Drawer/ClinicalEventForm';
import classNames from 'classnames';
import MonitorText from '../Text/MonitorText';
import MultipleLinesContent from './MultipleLinesContent';
import useCurrentClinic from '../../../../hooks/useCurrentClinic';
import { IClinicalVersion } from '../types';
import { formatTime, getDuration, getTimeZone } from '../../../../services/timeService';
import { unitOfTime, dateTimeFormat } from '../../../../services/timeService/types';

const EventContent = ({
  version,
  eventTime,
  timezone,
  showWarning,
}: {
  version?: IClinicalVersion;
  eventTime?: string;
  timezone?: string;
  showWarning?: boolean;
}) => {
  const { getMember } = useCurrentClinic();

  const assistanceLvl = assistanceLvlOptions?.find(
    (option) => option.value === version?.assistanceLvl,
  )?.label;

  const duration = getDuration(version?.durationInSeconds ?? 0, unitOfTime.seconds);

  const therapist = getMember(version?.userUuid ?? '');

  return (
    <div className={styles.eventContent}>
      <div className={styles.eventItem}>
        <div>Clinician</div>
        <div>{therapist ? therapist?.fullName : 'Unknown'}</div>
      </div>

      {version?.contact ? (
        <div className={styles.eventItem}>
          <div>Who was contacted</div>
          <div>{version.contact}</div>
        </div>
      ) : null}

      {version?.contactMethod ? (
        <div className={styles.eventItem}>
          <div>How they were contacted</div>
          <div>
            {
              eventContactMethodOptions?.find(
                (contactMethodOption) => contactMethodOption.value === version.contactMethod,
              )?.label
            }
          </div>
        </div>
      ) : null}

      <div
        className={classNames(styles.eventItem, {
          [styles.margin]: showWarning,
        })}
      >
        <div>{`Event time${timezone !== getTimeZone() ? ' (Local timezone)' : ''}`}</div>
        <div>{formatTime(eventTime, dateTimeFormat.DATE_TIME_FORMAT)}</div>
      </div>
      {showWarning ? (
        <div className="mb-4">
          <EventTimeWarning />
        </div>
      ) : null}
      {timezone !== getTimeZone() ? (
        <div className={styles.eventItem}>
          <div>Reported time (Custom timezone)</div>
          <div>{formatTime(eventTime, dateTimeFormat.DATE_TIME_FORMAT, timezone)}</div>
        </div>
      ) : null}

      {assistanceLvl ? (
        <div className={styles.eventItem}>
          <div>Level of assistance</div>
          <div>{assistanceLvl}</div>
        </div>
      ) : null}

      {version?.response ? (
        <div className={styles.eventItem}>
          <div>Response</div>
          <div>{version.response}</div>
        </div>
      ) : null}

      <div className={styles.eventItem}>
        <div>Duration (minutes)</div>
        <div>{duration.format('mm')}</div>
      </div>

      {version?.comment && version.eventName === eventType.monitoringAndAssessment ? (
        <div className="break-words">
          <DecisionText />
          <div className={classNames('mt-4', styles.eventItem)}>
            <div>
              <div>{commentMonitorInitialText?.split('.')[0]}.</div>
              <div>{commentMonitorInitialText?.split('.')[1]}</div>
            </div>
            <div>
              <MultipleLinesContent
                text={
                  version.comment
                    ?.replace(commentMonitorInitialText, '')
                    ?.split(educationToInitialText)?.[0]
                }
              />
            </div>
          </div>
          <div className={styles.eventItem}>
            <div className={styles.firstLetter}>{educationToInitialText}</div>
            <div>
              <MultipleLinesContent
                text={version.comment
                  ?.split(educationToInitialText)?.[1]
                  ?.replace(commentMonitorConditionsText, '')}
              />
            </div>
          </div>
          <MonitorText />
        </div>
      ) : (
        <div className={styles.eventItem}>
          <div>Onboarding details</div>
          <div className={'mb-4 break-all'}>
            <MultipleLinesContent text={version?.comment ?? ''} />
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(EventContent);
